import { fetchBaseQuery } from '@reduxjs/toolkit/query'
import { createApi } from '@reduxjs/toolkit/query/react'

import type { BasicColorIndex } from '../app/theme'
import type {
  BaseCompany,
  Company,
  JsonObject,
  NewTeam,
  Team,
  TeamGroup,
} from '../common/types/common'
import type {
  Application,
  ApplicationCreateInput,
  ApplicationUpdateInput,
} from '../common/types/dataCollectionsConfigs'
import type { ProcessFiltersStorageContent } from '../common/types/storage'
import type { DashboardInitialization, FileStore } from '../common/utils/UtilityWrapper/types'
import type {
  ArisIngestionApiLog,
  ArisIngestionCompanyConfig,
  ArisIngestionProcessConfigResponse,
  CreateLogResponse,
  StartArisIngestionApiRequest,
  StartArisIngestionApiResponse,
  UpsertProcessConfigRequest,
  UpsertProcessConfigResponse,
} from '../features/ArisIntegrationAdminPanelPage/types'
import { getAccessToken } from '../features/Authentication/authStorage'
import type {
  ConvertSocialTokenToAccessTokenPayload,
  ConvertedToken,
} from '../features/Authentication/types'
import type {
  ApplicationCategoriesResult,
  GroupedWindowUsageData,
  WindowUsageData,
} from '../features/BusinessApps/types'
import type {
  Activity,
  Attachment,
  CaseBase,
  CaseCategory,
  Collaborator,
  CreateAttachmentPayload,
  CreateCollaboratorPayload,
  CreateCommentPayload,
  DeleteAttachmentPayload,
  KanbanRes,
  Like,
  StrippedCase,
  UpdateAttachmentPayload,
  UpdateCaseOrderPayload,
  UpdateCasePayload,
  User,
} from '../features/Cases/types'
import type {
  CatalogApp,
  ConfigMetadata,
} from '../features/ConfigureDataCollectionAdminPanelPage/types'
import type {
  DataDeletionRequest,
  DeletionScopeEnum,
} from '../features/DataDeletionAdminPanelPage/types'
import type {
  UserInvitation,
  UserManagementUser,
} from '../features/ManageUsersAdminPanelPage/types'
import type {
  NetworkDataFlowsParams,
  NetworkDataFlowsResponse,
  NetworkNavigationsResponse,
} from '../features/Network/types'
import type { ProcessIdentifierMapper } from '../features/Overview/types'
import type { CustomDimensionData } from '../features/Processes/ProcessMining/Analysis/types'
import { ProcessProperties } from '../features/Processes/ProcessMining/types'
import type {
  ConformanceFlowsData,
  CreateConformanceFlowParams,
  DeleteConformanceFlowParams,
  EditConformanceFlowParams,
  GetConformanceFlowsParams,
  NewProcessAnalysisConfig,
  ProcessAnalysisConfig,
  ProcessDataflowsQueryOptions,
  UploadAttachmentConformanceFlowParams,
} from '../features/Processes/types'
import { WEB_APP_URL_DASHBOARD_BACKEND } from './configs'

const baseQuery = fetchBaseQuery({
  baseUrl: WEB_APP_URL_DASHBOARD_BACKEND,
  prepareHeaders: (headers) => {
    const token = getAccessToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)

    return headers
  },
})

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery,
  keepUnusedDataFor: 60 * 30, // in seconds -- https://redux-toolkit.js.org/rtk-query/usage/cache-behavior#reducing-subscription-time-with-keepunuseddatafor
  // TAGS >> https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#definitions
  tagTypes: [
    'DasboardSetup',
    'CaseCreated',
    'CaseActivity',
    'CaseAttachment',
    'CaseCategory',
    'User',
    'UserInvitation',
    'Company',
    'Team',
    'TeamUsernameList',
    'Application',
    'MyProfile',
    'TrackedProcess',
    'ConformanceFlows',
    'CompanyConfigFile',
    'CompanyConfigFileDB',
    'CompanyConfigFileTestReport',
    'TeamConfigFile',
    'Company',
    'TeamGroup',
    'CustomDimension',
    'UserTrend',
    'CompanyTrend',
    'CaseTrend',
    'DataDeletionRequest',
    'CatalogApps',
    'TemplateAppList',
    'ActiveRequirementFile',
    'xRayZipFile',
    'ArisCompanyConfig',
  ],
  endpoints: (build) => ({
    // ** Authentication ** //
    validateUserInvitation: build.query<void | { errMsg: string }, { email: string }>({
      query: (body) => ({
        url: '/accounts/validate-user-invitation',
        method: 'POST',
        body,
      }),
    }),
    getFileStoreToken: build.query<FileStore, void>({
      query: () => '/accounts/file-store-token',
    }),
    convertSocialTokenToAccessToken: build.mutation<
      ConvertedToken,
      ConvertSocialTokenToAccessTokenPayload
    >({
      query: (body) => ({
        url: '/auth/convert-token',
        method: 'POST',
        body,
      }),
    }),
    automaticSignIn: build.mutation<any, any>({
      query: (body) => ({
        url: '/auth/automatic-sign-in',
        method: 'POST',
        body,
      }),
    }),

    // ** Settings ** //
    getInitializeDashboard: build.query<DashboardInitialization, null>({
      query: () => '/configuration/initialize-dashboard',
      providesTags: ['DasboardSetup'],
    }),
    getUserCompanies: build.query<BaseCompany[], void>({
      query: () => '/accounts/company-list',
      providesTags: ['Company'],
    }),
    createCompany: build.mutation<
      void,
      { company_name: string; customer_status: string; agent_installation_target: number }
    >({
      query: (body) => ({
        url: '/company/companies',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Company'],
    }),
    changeUserCompany: build.mutation<any, string | number>({
      query: (companyId) => ({
        url: '/accounts/change-company',
        method: 'POST',
        body: { company_id: companyId },
      }),
      invalidatesTags: ['DasboardSetup', 'Company'],
    }),
    deleteUser: build.mutation<void, number>({
      query: (userId) => ({
        url: `/accounts/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),

    // ** Opportunities ** //
    // GET
    getKanban: build.query<KanbanRes, void>({
      query: () => '/cases/kanban',
      providesTags: ['CaseCreated'],
    }),
    getCaseActivity: build.query<Activity[], number>({
      query: (caseId) => `cases/${caseId}/activities`,
      providesTags: ['CaseCreated', 'CaseActivity'],
    }),
    getCaseAttachments: build.query<Attachment[], number>({
      query: (caseId) => `cases/${caseId}/attachments`,
      providesTags: ['CaseAttachment'],
    }),
    getUsers: build.query<User[], void | null>({
      query: () => '/cases/users',
      providesTags: ['User'],
    }),
    getCasesForTrendsDropdown: build.query<StrippedCase[], void>({
      query: () => '/cases/stripped',
      providesTags: ['CaseCreated'],
    }),

    // POST
    createCase: build.mutation<{ caseId: number }, { body: CaseBase }>({
      query: ({ body }) => ({
        url: `/cases/new`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CaseCreated'],
    }),
    createCaseTrend: build.mutation<any, { caseId: number; body: any }>({
      query: ({ caseId, body }) => ({
        url: `/data-trends/case-trends/${caseId}`,
        method: 'POST',
        body,
      }),
    }),
    createComment: build.mutation<null, CreateCommentPayload>({
      query: ({ caseId, body }) => ({
        url: `/cases/${caseId}/comments`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CaseActivity'],
    }),
    createLike: build.mutation<Like, { caseId: number }>({
      query: ({ caseId }) => ({
        url: `/cases/${caseId}/likes`,
        method: 'POST',
      }),
      invalidatesTags: ['CaseActivity'],
    }),
    createCollaborator: build.mutation<Collaborator, CreateCollaboratorPayload>({
      query: ({ caseId, body }) => ({
        url: `/cases/${caseId}/collaborators`,
        method: 'POST',
        body,
      }),
    }),
    createAttachment: build.mutation<Attachment[], CreateAttachmentPayload>({
      query: ({ caseId, body }) => ({
        url: `/cases/${caseId}/attachments`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CaseAttachment', 'CaseActivity'],
    }),

    // PUT
    updateCase: build.mutation<null, UpdateCasePayload>({
      query: ({ caseId, body }) => ({
        url: `/cases/${caseId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['CaseActivity'],
    }),
    updateAttachment: build.mutation<Attachment[], UpdateAttachmentPayload>({
      query: ({ caseId, attachmentId, body }) => ({
        url: `/cases/${caseId}/attachments/${attachmentId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['CaseAttachment', 'CaseActivity'],
    }),
    updateKanbanOrder: build.mutation<null, UpdateCaseOrderPayload>({
      query: ({ caseId, body }) => ({
        url: `cases/${caseId}/order/update`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['CaseActivity'],
    }),

    // DELETE
    deleteCase: build.mutation<null, number>({
      query: (caseId) => ({
        url: `/cases/${caseId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CaseCreated'],
    }),
    deleteAttachment: build.mutation<Attachment[], DeleteAttachmentPayload>({
      query: ({ caseId, attachmentId, body }) => ({
        url: `/cases/${caseId}/attachments/${attachmentId}`,
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['CaseAttachment', 'CaseActivity'],
    }),
    deleteLike: build.mutation<null, { caseId: number; likeId: number }>({
      query: ({ caseId, likeId }) => ({
        url: `/cases/${caseId}/likes/${likeId}`,
        method: 'DELETE',
      }),
    }),
    deleteCollaborator: build.mutation<null, { caseId: number; collaboratorId: number }>({
      query: ({ caseId, collaboratorId }) => ({
        url: `/cases/${caseId}/collaborators/${collaboratorId}`,
        method: 'DELETE',
      }),
    }),

    // ** Start ** //
    getProcessIdentifierMapper: build.query<ProcessIdentifierMapper, void>({
      query: () => `/process/tracked/identifier-mapper`,
      providesTags: [],
    }),

    // ** Legacy queries - Need to be moved to right microservices ** //
    // Business app
    getWindowUsage: build.query<WindowUsageData[] | GroupedWindowUsageData, any>({
      query: ({ body }) => ({
        url: '/business-apps/window-usage',
        method: 'POST',
        body,
      }),
    }),

    // Monitoring
    getAppMonitorStatus: build.query<any, any>({
      query: ({ body }) => ({
        url: '/business-apps/app-monitor-status',
        method: 'POST',
        body,
      }),
    }),

    // Network
    getNetworkDataFlows: build.query<NetworkDataFlowsResponse, NetworkDataFlowsParams>({
      query: (body) => ({
        url: '/business-apps/data-flows',
        method: 'POST',
        body,
      }),
    }),
    getNetworkNavigations: build.query<NetworkNavigationsResponse, NetworkDataFlowsParams>({
      query: (body) => ({
        url: '/business-apps/navigations',
        method: 'POST',
        body,
      }),
    }),
    getProcessDataflows: build.query<NetworkDataFlowsResponse, ProcessDataflowsQueryOptions>({
      query: ({ processId, ...body }) => ({
        url: `/business-apps/${processId}/process-data-flows`,
        method: 'POST',
        body,
      }),
    }),

    // ** PROCESSES - CONFIGS ** //
    getTrackedProcessesList: build.query<ProcessAnalysisConfig[], void>({
      query: () => ({
        url: '/process/tracked',
      }),
      providesTags: ['TrackedProcess'],
    }),
    createTrackedProcess: build.mutation<
      null,
      NewProcessAnalysisConfig | NewProcessAnalysisConfig[]
    >({
      query: (body) => ({
        url: '/process/tracked',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TrackedProcess'],
    }),
    updateTrackedProcess: build.mutation<null, { processId: string; body: ProcessAnalysisConfig }>({
      query: ({ processId, body }) => ({
        url: `/process/tracked/${processId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['TrackedProcess'],
    }),
    deleteTrackedProcess: build.mutation<null, string>({
      query: (processId) => ({
        url: `/process/tracked/${processId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TrackedProcess'],
    }),
    getTrackedProcessProperties: build.query<ProcessProperties, string>({
      query: (processId) => ({
        url: `/process/tracked/${processId}`,
        method: 'GET',
      }),
      providesTags: ['TrackedProcess'],
    }),
    getConformanceFlows: build.query<ConformanceFlowsData[], GetConformanceFlowsParams>({
      query: ({ processId, conformanceType }) => ({
        url: `/process/tracked/${processId}/defined-conformance`,
        method: 'GET',
        params: { conformanceType },
      }),
      providesTags: ['ConformanceFlows'],
    }),
    createConformanceFlow: build.mutation<null, CreateConformanceFlowParams>({
      query: ({ processId, body }) => ({
        url: `/process/tracked/${processId}/defined-conformance`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ConformanceFlows'],
    }),
    editConformanceFlow: build.mutation<null, EditConformanceFlowParams>({
      query: ({ processId, conformanceId, body }) => ({
        url: `/process/tracked/${processId}/defined-conformance/${conformanceId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['ConformanceFlows'],
    }),
    deleteConformanceFlow: build.mutation<null, DeleteConformanceFlowParams>({
      query: ({ processId, conformanceId }) => ({
        url: `/process/tracked/${processId}/defined-conformance/${conformanceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ConformanceFlows'],
    }),
    uploadConformanceAttachment: build.mutation<void, UploadAttachmentConformanceFlowParams>({
      query: ({ body, processId, conformanceId }) => ({
        url: `/process/tracked/${processId}/defined-conformance/${conformanceId}/attachment`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ConformanceFlows'],
    }),

    // ** PROCESSES - FILTER ** //
    getProcessFiltersUrl: build.query<
      { id: string; filters: ProcessFiltersStorageContent },
      string
    >({
      query: (filter_id) => ({
        url: `/process/tracked/filters/${filter_id}`,
        method: 'GET',
      }),
    }),
    createProcessFiltersUrl: build.mutation<
      { id: string; filters: ProcessFiltersStorageContent },
      { process_id: string; filters: ProcessFiltersStorageContent }
    >({
      query: (body) => ({
        url: `/process/tracked/filters`,
        method: 'POST',
        body,
      }),
    }),

    // ** PROCESSES - CUSTOM DIMENSIONS ** /
    getProcessCustomGroupByDimensions: build.query<CustomDimensionData[], string>({
      query: (processId) => ({
        url: `/process/tracked/${processId}/dimensions`,
        method: 'GET',
      }),
      providesTags: ['CustomDimension'],
    }),
    createCustomDimension: build.mutation<
      void,
      { processId: string; body: Omit<CustomDimensionData, 'id' | 'is_tag_dimension'> }
    >({
      query: ({ processId, body }) => ({
        url: `/process/tracked/${processId}/dimensions`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CustomDimension'],
    }),
    updateCustomDimension: build.mutation<
      void,
      { processId: string; dimensionId: number; body: CustomDimensionData }
    >({
      query: ({ processId, dimensionId, body }) => ({
        url: `/process/tracked/${processId}/dimensions/${dimensionId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['CustomDimension'],
    }),
    deleteCustomDimension: build.mutation<void, { processId: string; dimensionId: number }>({
      query: ({ processId, dimensionId }) => ({
        url: `/process/tracked/${processId}/dimensions/${dimensionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CustomDimension'],
    }),

    // ** Admin panel ** //
    getUserManagementList: build.query<
      { roleOptions: { [key: string]: string }; userList: UserManagementUser[] },
      void
    >({
      query: () => '/accounts/user-list',
      providesTags: ['User'],
    }),
    getUserInvitations: build.query<UserInvitation[], void>({
      query: () => '/accounts/user-invitations',
      providesTags: ['UserInvitation'],
    }),
    sendUserIntivation: build.mutation<UserInvitation, Pick<UserInvitation, 'email' | 'role'>>({
      query: (body) => ({
        url: '/accounts/user-invitations',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UserInvitation', 'User'],
    }),
    deleteUserInvititation: build.mutation<void, number>({
      query: (invitationId) => ({
        url: `/accounts/user-invitations/${invitationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserInvitation'],
    }),
    getTeams: build.query<Team[], void>({
      query: () => '/company/teams',
      providesTags: ['Team'],
    }),
    createTeams: build.mutation<Team, NewTeam>({
      query: (body) => ({
        url: `/company/teams`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Team', 'DasboardSetup'],
    }),
    updateTeams: build.mutation<Team, { teamId: number; body: NewTeam }>({
      query: ({ teamId, body }) => ({
        url: `/company/teams/${teamId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Team', 'DasboardSetup'],
    }),
    getTeamUsernameList: build.query<string[], string>({
      query: (invitationToken) => ({
        url: '/company/teams/username-list',
        params: { 'invitation-token': invitationToken },
      }),
      providesTags: ['TeamUsernameList'],
    }),
    updateTeamUsernameList: build.mutation<void, { invitationToken: string; usernameList: string }>(
      {
        query: ({ invitationToken, usernameList }) => ({
          url: `/company/teams/username-list`,
          method: 'PATCH',
          params: { 'invitation-token': invitationToken },
          body: { 'username-list': usernameList },
        }),
        invalidatesTags: ['TeamUsernameList'],
      },
    ),
    getCompanySettings: build.query<
      Pick<
        Company,
        'cost_per_hour' | 'currency' | 'currency_symbol_UTF8' | 'agent_installation_target'
      >,
      null
    >({
      query: () => '/company/company-settings',
      providesTags: ['Company'],
    }),
    getCaseCategories: build.query<CaseCategory[], void>({
      query: () => '/cases/categories',
      providesTags: ['CaseCategory'],
    }),
    updateUserRole: build.mutation<any, any>({
      query: ({ body }) => ({
        url: '/accounts/user-list',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    updateCompanySettings: build.mutation<
      any,
      {
        body: {
          cost_per_hour: number
          currency: string
          currency_symbol_UTF8: string
          agent_installation_target: number
        }
      }
    >({
      query: ({ body }) => ({
        url: '/company/company-settings',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Company', 'DasboardSetup'],
    }),
    updateCaseCategorySettings: build.mutation<
      any,
      { categoryId: number; body: { name: string; color_index: BasicColorIndex } }
    >({
      query: ({ categoryId, body }) => ({
        url: `/cases/categories/${categoryId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['CaseCategory'],
    }),
    createCaseCategory: build.mutation<
      null,
      { body: { name: string; color_index: BasicColorIndex } }
    >({
      query: ({ body }) => ({
        url: `/cases/categories`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CaseCategory'],
    }),
    deleteCaseCategorySettings: build.mutation<any, number>({
      query: (categoryId) => ({
        url: `/cases/categories/${categoryId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CaseCategory'],
    }),

    // ** Team Groups ** //
    getTeamGroup: build.query<TeamGroup[], void>({
      query: () => '/configuration/team-group',
      providesTags: ['TeamGroup'],
    }),
    createTeamGroup: build.mutation<null, { body: Omit<TeamGroup, 'group_id'> }>({
      query: ({ body }) => ({
        url: '/configuration/team-group',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TeamGroup'],
    }),
    updateTeamGroup: build.mutation<null, { id: string; body: Omit<TeamGroup, 'group_id'> }>({
      query: ({ id, body }) => ({
        url: `/configuration/team-group/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['TeamGroup'],
    }),
    deleteTeamGroup: build.mutation<null, { id: string }>({
      query: ({ id }) => ({
        url: `/configuration/team-group/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TeamGroup'],
    }),

    // Configs: What is deployed in prod.
    getCompanyConfigFileRaw: build.query<JsonObject, void>({
      query: () => ({
        url: '/config-studio/configs',
      }),
      providesTags: ['CompanyConfigFile'],
    }),
    getCompanyConfigMetadata: build.query<ConfigMetadata, void>({
      query: () => ({
        url: '/config-studio/configs/metadata',
      }),
      providesTags: ['CompanyConfigFile'],
    }),

    uploadCompanyConfig: build.mutation<void, void>({
      query: () => ({
        url: '/config-studio/configs',
        method: 'POST',
      }),
      invalidatesTags: ['CompanyConfigFile'],
    }),

    getTeamConfigDownloadUrl: build.query<string[], { invitationToken: string }>({
      query: ({ invitationToken }) => ({
        url: `/config-studio/configs/${invitationToken}`,
      }),
      providesTags: ['TeamConfigFile'],
    }),
    getTeamConfigMetadata: build.query<ConfigMetadata, { invitationToken: string }>({
      query: ({ invitationToken }) => ({
        url: `/config-studio/configs/${invitationToken}/metadata`,
      }),
      providesTags: ['TeamConfigFile'],
    }),
    uploadTeamConfig: build.mutation<void, { invitationToken: string; body: JsonObject }>({
      query: ({ body, invitationToken }) => ({
        url: `/config-studio/configs/${invitationToken}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TeamConfigFile'],
    }),
    deleteTeamConfig: build.mutation<void, { invitationToken: string }>({
      query: ({ invitationToken }) => ({
        url: `/config-studio/configs/${invitationToken}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TeamConfigFile'],
    }),

    updateConfigSource: build.mutation<
      null,
      { invitationToken: string; body: { configuration_source: string } }
    >({
      query: ({ invitationToken, body }) => ({
        url: `/config-studio/configs/${invitationToken}/configuration-source`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['TeamConfigFile', 'CompanyConfigFile', 'Team'],
    }),
    getToolingLink: build.query<string, void>({
      query: () => '/config-studio/tooling-link',
    }),

    // ** Catalog - OLD ** //
    getCatalogAppList: build.query<CatalogApp[], void>({
      query: () => ({
        url: '/config-studio/catalog-apps',
      }),
      providesTags: ['CatalogApps'],
    }),

    // ** Data Collection Templates ** //
    getTemplateDashboardAppData: build.query<Application[], void>({
      query: () => '/config-studio/templates/applications',
    }),
    getTemplateProcessData: build.query<ProcessAnalysisConfig[], void>({
      query: () => '/config-studio/templates/processes',
    }),

    // ** Data collection configs ** //
    // Applications
    getDashboardAppData: build.query<Application[], void>({
      query: () => '/data-collection-configs/applications',
      providesTags: ['Application'],
    }),
    createDataCollectionsConfigApplication: build.mutation<
      Application,
      ApplicationCreateInput | ApplicationCreateInput[]
    >({
      query: (body) => ({
        url: '/data-collection-configs/applications/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Application'],
    }),
    updateDataCollectionsConfigApplication: build.mutation<Application, ApplicationUpdateInput>({
      query: ({ id, ...body }) => ({
        url: `/data-collection-configs/applications/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Application'],
    }),
    deleteDataCollectionsConfigApplication: build.mutation<Application, string>({
      query: (id) => ({
        url: `/data-collection-configs/applications/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Application'],
    }),

    getInstallerLink: build.query<string | null, void>({
      query: () => '/data-collection-configs/installer-link',
    }),
    getApplicationCategories: build.query<ApplicationCategoriesResult, void>({
      query: () => '/data-collection-configs/application-categories',
      providesTags: ['Application'],
    }),

    // ** Data Deletion ** //
    getDataDeletionRequests: build.query<DataDeletionRequest[], void>({
      query: () => ({
        url: '/utils/data-deletion-requests',
      }),
      providesTags: ['DataDeletionRequest'],
    }),
    createDataDeletionRequest: build.mutation<
      void,
      { deletion_scope: DeletionScopeEnum; team_id: null | string }
    >({
      query: (body) => ({
        url: '/utils/data-deletion-requests',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DataDeletionRequest'],
    }),
    updateDataDeletionRequest: build.mutation<void, number>({
      query: (id) => ({
        url: `/utils/data-deletion-requests/${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['DataDeletionRequest'],
    }),

    // ** Aris Integration ** //
    startArisIngestionApi: build.mutation<
      StartArisIngestionApiResponse,
      StartArisIngestionApiRequest
    >({
      query: (body) => ({
        url: `/aris-ingestion-api/start-aris-ingestion-api`,
        method: 'POST',
        body,
      }),
    }),

    getLog: build.query<ArisIngestionApiLog, { logId: string }>({
      query: ({ logId }) => `/aris-ingestion-api/log/${logId}`,
    }),

    getArisIngestionApiLogStatus: build.query<ArisIngestionApiLog, { logId: string }>({
      query: ({ logId }) => `/aris-ingestion-api/get-log/${logId}`,
    }),

    getArisCompanyConfig: build.query<ArisIngestionCompanyConfig, void>({
      query: () => `/aris-ingestion-api/company-config`,
      providesTags: [{ type: 'ArisCompanyConfig', id: 'CONFIG' }],
    }),

    getArisArisIngestionProcessConfig: build.query<
      ArisIngestionProcessConfigResponse,
      { processId: string }
    >({
      query: ({ processId }) => `/aris-ingestion-api/process-config/${processId}`,
    }),

    createArisIngestionApiLog: build.mutation<
      CreateLogResponse,
      { processId: string; requestedByUserId: number }
    >({
      query: ({ processId, requestedByUserId }) => ({
        url: `/aris-ingestion-api/create-log`,
        method: 'POST',
        body: {
          process_id: processId,
          requested_by_user_id: requestedByUserId,
        },
      }),
    }),

    upsertArisConfig: build.mutation<
      { message: string },
      {
        tenantUrl: string
        clientId: string
        clientSecret: string
      }
    >({
      query: ({ tenantUrl, clientId, clientSecret }) => ({
        url: `/aris-ingestion-api/upsert-config`,
        method: 'POST',
        body: {
          tenant_url: tenantUrl,
          client_id: clientId,
          client_secret: clientSecret,
        },
      }),
      invalidatesTags: [{ type: 'ArisCompanyConfig', id: 'CONFIG' }],
    }),

    upsertArisProcessConfig: build.mutation<
      UpsertProcessConfigResponse,
      UpsertProcessConfigRequest
    >({
      query: (body) => ({
        url: `/aris-ingestion-api/upsert-process-config`,
        method: 'POST',
        body,
      }),
    }),

    // ** Utils ** //
    getDynamicPost: build.query<any, { url: string; body: object }>({
      query: ({ url, body }) => ({
        url,
        method: 'POST',
        body,
      }),
    }),
  }),
})

// Naming rules for hooks below:
// 1) use, the normal prefix for any React hook
// 2) The name of the endpoint, capitalized
// 3) The type of the endpoint, Query OR Mutation
// use<endpoint_name_capitalized><Query|Mutation>
export const {
  // ** Auth ** //
  useLazyValidateUserInvitationQuery,
  useLazyGetFileStoreTokenQuery,
  useConvertSocialTokenToAccessTokenMutation,
  useAutomaticSignInMutation,

  // ** Settings ** //
  useGetInitializeDashboardQuery,
  useGetUserCompaniesQuery,
  useCreateCompanyMutation,
  useChangeUserCompanyMutation,
  useDeleteUserMutation,

  // ** Cases ** //
  useGetKanbanQuery,
  useGetCaseActivityQuery,
  useGetUsersQuery,
  useGetCasesForTrendsDropdownQuery,
  useGetCaseAttachmentsQuery,

  useCreateCaseMutation,
  useCreateCommentMutation,
  useCreateCaseTrendMutation,
  useCreateAttachmentMutation,
  useCreateLikeMutation,
  useCreateCollaboratorMutation,

  useUpdateCaseMutation,
  useUpdateAttachmentMutation,
  useUpdateKanbanOrderMutation,

  useDeleteCaseMutation,
  useDeleteAttachmentMutation,
  useDeleteLikeMutation,
  useDeleteCollaboratorMutation,

  // ** Start ** //
  useGetProcessIdentifierMapperQuery,

  // ** Business Apps ** //
  useGetWindowUsageQuery,
  useLazyGetWindowUsageQuery,

  // ** Work ** //
  useGetNetworkDataFlowsQuery,
  useGetNetworkNavigationsQuery,

  // ** Processes ** //
  useGetTrackedProcessesListQuery,
  useCreateTrackedProcessMutation,
  useUpdateTrackedProcessMutation,
  useDeleteTrackedProcessMutation,
  useGetConformanceFlowsQuery,
  useGetTrackedProcessPropertiesQuery,
  useCreateConformanceFlowMutation,
  useEditConformanceFlowMutation,
  useDeleteConformanceFlowMutation,
  useUploadConformanceAttachmentMutation,
  useGetProcessCustomGroupByDimensionsQuery,
  useCreateCustomDimensionMutation,
  useUpdateCustomDimensionMutation,
  useDeleteCustomDimensionMutation,
  useGetProcessDataflowsQuery,

  // ** PROCESSES - FILTERS ** //
  useLazyGetProcessFiltersUrlQuery,
  useCreateProcessFiltersUrlMutation,

  // ** Admin panel ** //
  useGetUserManagementListQuery,
  useGetUserInvitationsQuery,
  useSendUserIntivationMutation,
  useDeleteUserInvititationMutation,
  useGetTeamsQuery,
  useCreateTeamsMutation,
  useUpdateTeamsMutation,
  useGetTeamUsernameListQuery,
  useUpdateTeamUsernameListMutation,
  useGetCompanySettingsQuery,
  useGetCaseCategoriesQuery,

  useUpdateCompanySettingsMutation,
  useUpdateCaseCategorySettingsMutation,
  useUpdateUserRoleMutation,
  useCreateCaseCategoryMutation,
  useDeleteCaseCategorySettingsMutation,

  // ** Team Groups ** //
  useGetTeamGroupQuery,
  useCreateTeamGroupMutation,
  useUpdateTeamGroupMutation,
  useDeleteTeamGroupMutation,

  // ** Config Studio ** //
  useLazyGetCompanyConfigFileRawQuery,
  useGetCompanyConfigFileRawQuery,
  useLazyGetTeamConfigDownloadUrlQuery,
  useGetCompanyConfigMetadataQuery,
  useGetTeamConfigMetadataQuery,
  useUpdateConfigSourceMutation,
  useUploadCompanyConfigMutation,
  useUploadTeamConfigMutation,
  useDeleteTeamConfigMutation,
  useGetToolingLinkQuery,
  useGetCatalogAppListQuery,

  // ** Data Collection Templates ** //
  useGetTemplateDashboardAppDataQuery,
  useGetTemplateProcessDataQuery,

  // ** Tracked apps and categories ** //
  useGetDashboardAppDataQuery,
  useGetApplicationCategoriesQuery,

  // Installer link
  useGetInstallerLinkQuery,

  // ** Data collection configs ** //
  useCreateDataCollectionsConfigApplicationMutation,
  useUpdateDataCollectionsConfigApplicationMutation,
  useDeleteDataCollectionsConfigApplicationMutation,

  // ** Monitoring ** //
  useGetAppMonitorStatusQuery,

  // ** Data Deletion ** //
  useGetDataDeletionRequestsQuery,
  useCreateDataDeletionRequestMutation,
  useUpdateDataDeletionRequestMutation,

  // ** Aris Integration ** //
  useStartArisIngestionApiMutation,
  useGetLogQuery,
  useGetArisIngestionApiLogStatusQuery,
  useGetArisCompanyConfigQuery,
  useGetArisArisIngestionProcessConfigQuery,
  useLazyGetArisArisIngestionProcessConfigQuery,
  useCreateArisIngestionApiLogMutation,
  useUpsertArisConfigMutation,
  useUpsertArisProcessConfigMutation,

  // ** Utils ** //
  usePrefetch, // https://redux-toolkit.js.org/rtk-query/usage/prefetching
  useGetDynamicPostQuery,
} = apiSlice

